import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp, TrendingDown, Clock, BarChart3, ArrowRight,
  Activity, Plane, Star, ChevronRight,
} from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import LiveBusiestCTA from '@/components/LiveBusiestCTA';
import HourlyPatternChart from '@/components/HourlyPatternChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LEVEL_PILL = {
  low:     'bg-blue-50 text-blue-700',
  medium:  'bg-indigo-50 text-indigo-700',
  high:    'bg-amber-50 text-amber-700',
  extreme: 'bg-rose-50 text-rose-700',
};

export default function InsightsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/insights/national`);
        if (r.ok && !cancel) setData(await r.json());
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Airport Crowd Insights — Live National Traffic Patterns"
        description="Live, data-driven insights into US airport crowd patterns: the busiest and quietest hubs right now, hour-by-hour national traffic, and what time of day actually feels emptiest at major airports."
        path="/insights"
        keywords="airport insights, national airport traffic, busiest US hubs, quietest airports, airport crowd patterns, hour-by-hour TSA, airport data analysis"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Insights', url: '/insights' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Dataset',
            name: 'National US Airport Crowd Patterns',
            description: 'Aggregated hourly crowd-level data across 8 major US airport hubs derived from the How Busy Is My Airport prediction model.',
            url: 'https://howbusyismyairport.com/insights',
            creator: { '@type': 'Organization', name: 'How Busy Is My Airport' },
            license: 'https://howbusyismyairport.com/terms',
          },
        ]}
      />

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-6xl flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0" data-testid="brand-link">
            <AppLogo size={36} className="flex-shrink-0" />
            <h1 className="heading text-sm sm:text-base font-semibold tracking-tight text-slate-900 leading-[1.15]">
              How Busy Is My Airport
            </h1>
          </Link>
          <LiveBusiestCTA />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-6xl">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8 max-w-3xl"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
            <Activity className="w-3 h-3" />
            Data hub
          </span>
          <h2 className="heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            What US airports actually look like — by the hour.
          </h2>
          <p className="mt-2 text-sm sm:text-[15px] text-slate-500">
            We crunch the typical week across 8 major US hubs to surface patterns you can't see on any flight-tracking app: the quietest hours nationally, the busiest hubs by average crowd score, and the time of day most travelers should avoid.
          </p>
        </motion.div>

        {loading || !data ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="border-slate-200 bg-white">
                <CardContent className="p-5">
                  <div className="h-32 bg-slate-100 rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Top stats */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard
                icon={<BarChart3 className="w-4 h-4 text-blue-500" />}
                label="Hubs tracked"
                value={data.hubs_tracked}
                sub="continuously sampled"
              />
              <StatCard
                icon={<TrendingUp className="w-4 h-4 text-rose-500" />}
                label="Peak hour nationally"
                value={data.peak_hour?.label}
                sub="when to avoid TSA"
              />
              <StatCard
                icon={<TrendingDown className="w-4 h-4 text-emerald-500" />}
                label="Quietest hour"
                value={data.quietest_hour?.label}
                sub="best time to fly"
              />
              <StatCard
                icon={<Activity className="w-4 h-4 text-indigo-500" />}
                label="Avg crowd score"
                value={data.national_avg_score?.toFixed(1)}
                sub="across daytime hours"
              />
            </div>

            {/* National hour-by-hour */}
            <HourlyPatternChart
              data={data.hourly_national}
              title="National crowd-score by hour"
              subtitle="Averaged across the 8 hubs we track — taller bars = more crowded TSA + concourses."
              dataTestId="national-hourly-chart"
            />

            {/* Busiest vs Quietest hubs */}
            <div className="grid md:grid-cols-2 gap-4">
              <HubList
                title="Busiest hubs (typical day)"
                subtitle="By average crowd score across daytime hours"
                items={data.busiest_hubs}
                accent="rose"
                testId="busiest-hubs-list"
              />
              <HubList
                title="Quietest hubs (typical day)"
                subtitle="Easier-going alternatives if you can pick"
                items={data.quietest_hubs}
                accent="emerald"
                testId="quietest-hubs-list"
              />
            </div>

            {/* Methodology blurb */}
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5 sm:p-6">
                <h3 className="text-base font-semibold text-slate-900 heading mb-2">How we compute this</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We simulate a full typical week at each of the 8 major US hubs using the same crowd model we use for live predictions —
                  weighting departures, aircraft size, and time-of-day. Each hour is then averaged across all 7 days to surface the
                  underlying pattern that holds week-over-week. Numbers update hourly. For live snapshots, see the{' '}
                  <Link to="/live" className="text-blue-600 hover:underline">Live busiest dashboard</Link>{' '}
                  or our <Link to="/methodology" className="text-blue-600 hover:underline">methodology page</Link>.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Static editorial — always rendered so the page carries substance even before data loads */}
        <section className="mt-10 max-w-3xl" data-testid="insights-editorial">
          <h3 className="heading text-xl sm:text-2xl font-semibold text-slate-900 mb-3">How to read these numbers</h3>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-4">
            The crowd score is a 0–10 index of expected passenger pressure inside the terminal — security queues,
            concourse density, and gate-area seating. It's built from scheduled departures weighted by aircraft
            capacity, then adjusted for time of day and calibrated against real traveler feedback. A score under 4
            generally means walk-up security; above 7 means budget serious extra time.
          </p>
          <h3 className="heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">The three national rush hours</h3>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-4">
            Across every week we sample, US airport traffic forms three predictable waves. The first is the
            <strong> 7–10 AM departure bank</strong>, driven by business travel and first-flights-out. The second is a
            softer <strong>midday plateau</strong> around lunchtime connections. The third — and usually the heaviest —
            is the <strong>3–6 PM crush</strong>, when afternoon connection banks at hubs like{' '}
            <Link to="/airports/atl" className="text-blue-600 hover:underline">ATL</Link> and{' '}
            <Link to="/airports/dfw" className="text-blue-600 hover:underline">DFW</Link> stack on top of local
            leisure departures. International gateways like{' '}
            <Link to="/airports/jfk" className="text-blue-600 hover:underline">JFK</Link> add a fourth, evening wave
            as long-haul flights push out between 5 and 9 PM.
          </p>
          <h3 className="heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">What travelers consistently get wrong</h3>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-4">
            The most common mistake is treating all early flights as easy. At connection-heavy megahubs the pre-dawn
            security spike can rival the afternoon peak, while the famous "avoid Friday" rule matters far less at
            origin-and-destination airports like LAS or MCO, where Sunday afternoons are the real test. The reliable
            national trough is mid-morning — roughly 9:30 to 11 AM — followed by the post-rush early evening. If your
            schedule is flexible, those two windows beat any security shortcut. For a specific trip, run a{' '}
            <Link to="/" className="text-blue-600 hover:underline">live prediction for your airport and flight time</Link>{' '}
            — it accounts for the actual schedule on your travel day rather than the typical-week average shown here.
          </p>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-medium">
          {icon}
          {label}
        </div>
        <div className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-2 heading">{value ?? '—'}</div>
        <div className="text-xs text-slate-400 mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
}

function HubList({ title, subtitle, items = [], accent = 'rose', testId }) {
  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-5 sm:p-6" data-testid={testId}>
        <h3 className="text-base font-semibold text-slate-900 heading">{title}</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-4">{subtitle}</p>
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={it.code} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0">
              <Link
                to={`/airports/${it.code.toLowerCase()}`}
                className="flex items-center gap-3 group flex-1 min-w-0"
                data-testid={`hub-link-${it.code}`}
              >
                <span className={`text-[11px] font-bold w-5 text-right ${accent === 'rose' ? 'text-rose-500' : 'text-emerald-500'}`}>
                  #{idx + 1}
                </span>
                <span className="font-mono font-semibold text-slate-900">{it.code}</span>
                <span className="text-sm text-slate-500 truncate group-hover:text-slate-800">{it.city}</span>
              </Link>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${LEVEL_PILL[it.crowd_level] || LEVEL_PILL.medium}`}>
                {it.avg_crowd_score.toFixed(1)}
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
